<template>
    <div
        v-show="visible"
        :id="modalId"
        class="modal"
        role="dialog"
        aria-modal="true"
    >
        <div
            class="modal-backdrop"
            tabindex="-1"
            @click="close"
        />

        <div class="modal-frame">
            <div
                ref="contentRef"
                class="modal-content"
                role="document"
            >
                <slot />
                <button
                    type="button"
                    :class="`close-button ${closeButtonClass}`"
                    @click="close"
                >
                    {{ t('apply') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, watch, provide, useId } from 'vue'
import { TELEPORT_ID_KEY } from 'widget-components/composables/teleport/consts'
import { useI18n } from 'widget-components/utilities/i18n'

const modalId = useId()
const { t } = useI18n()

const visible = defineModel<boolean>()

defineProps<{
    closeButtonClass?: string
}>()

const emit = defineEmits<{
    (e: 'close'): void
}>()

const close = () => {
    visible.value = false
    document.body.style.overflow = ''
    emit('close')
}

const contentRef = ref<HTMLElement | null>(null)

const handleKeyboard = (e: KeyboardEvent) => {
    if (!visible.value) {
        return
    }

    if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        close()
    }

    if (e.key === 'Tab') {
        const focusables = contentRef.value?.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )

        if (!focusables || focusables.length === 0) {
            return
        }

        const first = focusables[0]
        const last = focusables[focusables.length - 1]

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault()
            last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault()
            first.focus()
        }
    }
}

watch(visible, (v) => {
    if (v) {
        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', handleKeyboard)
    } else {
        document.removeEventListener('keydown', handleKeyboard)
    }
}, { immediate: true })

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeyboard)
})

provide(TELEPORT_ID_KEY, modalId)
</script>

<style scoped lang="sass">
.modal
    position: fixed
    inset: 0
    display: flex
    align-items: center
    justify-content: center
    z-index: var(--layer-component-popover, 1000)

.modal-backdrop
    position: absolute
    inset: 0
    background-color: rgba(0, 0, 0, 0.5)
    backdrop-filter: saturate(180%) blur(3px)

.modal-frame
    border-radius: var(--widget-border-radius)
    z-index: 1
    min-width: min(90vw, 1000px)
    max-width: 90vw
    max-height: 90vh
    overflow: hidden
    display: flex
    flex-direction: column

.modal-content
    position: relative
    background: white
    padding: 20px
    width: 100%
    height: 100%
    overflow: auto
    flex: 1

:where(.close-button) // to force lowest specificity
    appearance: button
    background-color: transparent
    font-family: inherit
    font-size: 100%
    border: 0
    display: flex
    align-items: center
    justify-self: flex-end
    justify-content: center
    cursor: pointer
    padding: 10px
    border-radius: 8px
    transition: .2s ease-in-out all

    &:hover
        background: var(--color-black-tint-invisible)
</style>
